import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { BrowserModule } from '@angular/platform-browser';
import { provideStore } from '@ngxs/store';
import { provideTranslateService, provideTranslateLoader, TranslateService } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { provideRouter, Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { catchError, map, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { UnauthorizedInterceptor } from './auth/services/unauthorized-interceptor.service';
import { provideTheme, SnackbarService } from '@siemens/component-lib';
import { MsalRoleGuard } from './shared/guards/role-based-msal.guard';
import { WorkflowDataState } from './shared/state/workflow/workflow-data.state';
import { WorkflowConfigState } from './shared/state/workflow/workflow-config.state';
import { ExcelUploadState } from './shared/state/workflow/excel-upload.state';
import { SnowflakeSearchState } from './shared/state/snowflake/snowflake-search.state';
import { provideSiAgGridConfig, AgNoRowsOverlayComponent, AgDateInputComponent } from '@siemens/element-ng/ag-grid';
import { SiDateCellEditorComponent } from './shared/components/ag-grid';
import { MultiTranslationLoader } from './core/multi-translation-loader.service';

const msalInstanceFactory = () => new PublicClientApplication(environment.msalConfig);

const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        protectedResourceMap.set(`${item.uri}/*`, item.scopes);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
};

const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig[0].scopes] },
    loginFailedRoute: '/unauthorized'
});

function initializeMsal(msalService: MsalService, router: Router) {
    return () =>
        msalService.handleRedirectObservable().pipe(
            map((): undefined => {
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                void router.navigateByUrl('unauthorized');
                return of(undefined);
            }),
            take(1)
        );
}

function initializeLanguage(translateService: TranslateService) {
    return () => {
        registerLocaleData(localeDe);
        translateService.setFallbackLang('en');
        translateService.use('en');
        return of(undefined);
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(BrowserModule),
        provideTranslateService({
            loader: provideTranslateLoader(MultiTranslationLoader),
            fallbackLang: 'en',
            lang: 'en'
        }),
        SnackbarService,
        provideTheme('sag'),
        provideRouter(routes),
        provideAnimations(),
        provideSiAgGridConfig('standard', {
            //  provideSiAgGridConfig shallow-merges gridOptions.components, so re-declare the Element defaults
            // (agDateInput/agNoRowsOverlay) here too, otherwise this override wipes them out entirely
            components: {
                agDateInput: AgDateInputComponent,
                agNoRowsOverlay: AgNoRowsOverlayComponent,
                siDateCellEditor: SiDateCellEditorComponent
            }
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideStore(
            [WorkflowDataState, WorkflowConfigState, ExcelUploadState, SnowflakeSearchState],
            {
                compatibility: {
                    strictContentSecurityPolicy: true
                }
            },
            withNgxsReduxDevtoolsPlugin()
        ),
        {
            provide: MSAL_INSTANCE,
            useFactory: msalInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: msalGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: msalInterceptorConfigFactory
        },
        MsalService,
        MsalRoleGuard,
        MsalBroadcastService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService), inject(Router));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeLanguage(inject(TranslateService));
            return initializerFn();
        })
    ]
};
